'use client'

import Link from 'next/link'

// `outline` is the quiet form for an empty state, where the hint is the only
// thing on screen. `solid` is for pages whose content competes for attention —
// next to a section heading it has to read as an action, not a caption.
//
// The two differ in fill only: same border width, no shadow, and a hover that
// doesn't resize. A shadow or a hover scale on one of them makes a matched pair
// sitting side by side read as two different sizes.
const VARIANTS = {
  outline:
    'border border-[var(--foreground-bg-color)] text-white hover:bg-[var(--foreground-bg-color)]',
  solid:
    'border border-white/20 bg-[var(--main-bg-color)] text-white hover:opacity-90',
}

const ICON_TONE = {
  outline: 'text-[var(--main-bg-color)]',
  solid: 'text-white',
}

// Sizing lives here rather than in a caller's `className` because Tailwind
// resolves conflicts by stylesheet order, not class order — a caller passing
// `h-10` would lose to the base `h-11` unpredictably.
const SIZES = {
  md: 'h-11 px-5 text-sm gap-2',
  // Compact on phones so it still fits on one line beside a section heading;
  // identical to `md` from md up.
  compact: 'h-10 px-4 text-xs gap-1.5 md:h-11 md:px-5 md:text-sm md:gap-2',
}

// Fixed-width icon slot, so the label always starts at the same offset no
// matter which set an icon comes from — react-icons glyphs (Fa vs Bs) have
// different intrinsic widths at the same `size`, which reads as uneven padding
// when two pills sit next to each other.
const ICON_SLOT = { md: 'w-4', compact: 'w-3.5 md:w-4' }
const ICON_SIZES = { md: 16, compact: 14 }

/**
 * Cross-navigation hint: a pill that signposts the next surface from the one
 * you're on (Explore → Create, Create → Explore).
 *
 * Below md the header nav collapses into the hamburger drawer, so a visitor who
 * never opens it only ever sees the page they landed on. These in-content hints
 * are the path between surfaces for that visitor; they stay at larger sizes as
 * plain CTAs, where they cost one line and reinforce the header nav.
 *
 * Tap target is 44px tall by default, 40px in the `compact` size — both clear
 * WCAG 2.5.8's 24px minimum.
 */
export default function NavHint({
  href,
  icon: Icon,
  label,
  onClick,
  variant = 'outline',
  size = 'md',
  className = '',
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`inline-flex items-center whitespace-nowrap rounded-full font-semibold transition duration-200 ${SIZES[size]} ${VARIANTS[variant]} ${className}`}
    >
      <span
        className={`flex shrink-0 justify-center ${ICON_SLOT[size]} ${ICON_TONE[variant]}`}
      >
        <Icon size={ICON_SIZES[size]} />
      </span>
      {label}
    </Link>
  )
}
